import {Fragment, useRef, useState} from 'react';
import type {ChangeEvent, ComponentPropsWithRef, ComponentType, ReactNode} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {Alert, Button, Checkbox, DatePicker, Empty, Flex, Form, Input, Popconfirm, Segmented, Table, Tag, Tooltip, Typography, type CheckboxChangeEvent, type SegmentedProps, type TableColumnsType} from 'antd';
import {
    CheckCircleOutlined,
    CheckOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    FileTextOutlined,
    FolderAddOutlined,
    InboxOutlined,
    InfoCircleOutlined,
    MinusCircleOutlined,
    PlusOutlined,
    SearchOutlined,
    ShopOutlined,
    SwapOutlined,
    ThunderboltOutlined,
    UploadOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import {DragAndDropUploadForPdfOrXml} from 'diferpan-common-web'
import { useAppMessage } from '@providers'
import {
    buscarFilialDaUf,
    buscarUfDaFilial,
    extractNumbers,
    isArquivoXml,
    lerDestinatarioDoArquivoXml,
    lerNotaFiscalDoXml,
    lerNumeroConhecimentoDoXml,
    lerTextoDoArquivo,
    mapFilesToDto,
    resolverMensagemErro,
} from '@utils';
import type {DestinatarioNotaFiscal} from '@utils';
import { analisarAnexosAgendamento } from '@utils/anexos-agendamento'
import {classificarConferenciaUf, resolverMarcaConferenciaNota} from '@utils/nota-fiscal-filial'
import type {ConferenciaUfNota} from '@utils/nota-fiscal-filial'
import {useAgendamentoResumoQuery, useBuscarNfeTributum, useConcluirPreAgendamentoMutation} from "@queries";
import {useGlobalFilial} from "@hooks";
import './style.scss';
import type { AgendaExternaCifDto, AgendaExternaFobDto } from '../../types';

const {Text} = Typography;

interface UploadRef {
    clearFiles(): void;
}

type ArquivoUpload = File & { uid: string };

type TipoFrete = 'CIF' | 'FOB';

interface ConferenciaNotaLista {
    marca: ConferenciaUfNota;
    ufDestinatario: string | null;
}

interface FinalizarFormValues {
    chaveNfe?: string;
    numeroConhecimento?: number | string;
    dataEmissao?: Dayjs | null;
    razaoSocialFornecedor?: string;
    volumeCarga?: number | string;
}

const MENSAGEM_FILIAL_SEM_UF = 'Não foi possível determinar a UF da filial selecionada, então o destinatário da Nota Fiscal não pode ser conferido.';

const FORMATO_DATA = 'DD/MM/YYYY';

const normalizarSomenteDigitos = (valor: unknown) => extractNumbers(valor) ?? '';

const converterNumeroConhecimento = (valor: number | string | undefined) => (valor === undefined || valor === '' ? undefined : Number(valor));

const somarVolumes = (notas: AgendaExternaCifDto[]) => notas.reduce((total, nota) => total + Number(nota.volumeCarga || 0), 0);

const contarPdf = (arquivos: ArquivoUpload[]) => arquivos.filter(arquivo => arquivo.type === 'application/pdf').length;

const contarXml = (arquivos: ArquivoUpload[]) => arquivos.filter(isArquivoXml).length;

const pluralizar = (quantidade: number, singular: string, plural: string) => (quantidade === 1 ? singular : plural);

const contarMarcas = (conferencias: Record<string, ConferenciaNotaLista>, marca: ConferenciaUfNota) =>
    Object.values(conferencias).filter(conferencia => conferencia.marca === marca).length;

const renderMarcadorObrigatorio = (rotulo: ReactNode, {required}: {required: boolean}) => (
    <>
        {rotulo}
        {required && <span className="form-field__obrigatorio" aria-hidden="true">*</span>}
    </>
);

// A diferpan-common-web declara os wrappers DragAndDropUploadFor* com BaseUploadProps,
// que nao inclui maxCount — mas o runtime repassa a prop por spread para DragAndDropUpload
// (dist/esm/components/DragAndDropUpload/index.js:387), onde ela vale.
// TODO: Claude 11/09/2026 | remover quando a lib declarar maxCount no contrato dos wrappers
const UploadPdfOuXml = DragAndDropUploadForPdfOrXml as ComponentType<
    ComponentPropsWithRef<typeof DragAndDropUploadForPdfOrXml> & {maxCount?: number}
>;

export const FinalizarAgendamentoPage = () => {
    const message = useAppMessage();
    const navigate = useNavigate();
    const {agendaId} = useParams();
    const buscarNfeTributum = useBuscarNfeTributum();
    const concluirMutation = useConcluirPreAgendamentoMutation();
    const resumoQuery = useAgendamentoResumoQuery({agendaId: agendaId ?? null, aberto: true});
    const [filial, definirFilial] = useGlobalFilial();
    const NFUploadRef = useRef<UploadRef | null>(null);
    const CTOUploadRef = useRef<UploadRef | null>(null);
    const inputXmlConferenciaRef = useRef<HTMLInputElement | null>(null);
    const [form] = Form.useForm<FinalizarFormValues>();
    const chaveNfe = Form.useWatch('chaveNfe', form);
    const [isFob, setIsFob] = useState(false);
    const [formRequestFobDto, setFormRequestFobDto] = useState<AgendaExternaFobDto | null>(null);
    const [formRequestGeralDto, setFormRequestGeralDto] = useState<AgendaExternaFobDto[]>([]);
    const [formRequestNotasDto, setFormRequestNotasDto] = useState<AgendaExternaCifDto[]>([]);
    const [fobFiles, setFobFiles] = useState<ArquivoUpload[]>([]);
    const [cifFiles, setCifFiles] = useState<ArquivoUpload[]>([]);
    const [buttonTributumLoading, setButtonTributumLoading] = useState(false);
    const [destinatarioNf, setDestinatarioNf] = useState<DestinatarioNotaFiscal | null>(null);
    const [chaveNfeFilialConfirmada, setChaveNfeFilialConfirmada] = useState<string | null>(null);
    const [cienciaUfDivergente, setCienciaUfDivergente] = useState<DestinatarioNotaFiscal | null>(null);
    const [conferenciasPorChave, setConferenciasPorChave] = useState<Record<string, ConferenciaNotaLista>>({});
    const [chaveNotaEmConferencia, setChaveNotaEmConferencia] = useState<string | null>(null);

    const validateNumeroConhecimento = (_: unknown, value: unknown) => {
        if (isFob && !value) {
            return Promise.reject(new Error('Campo obrigatório!'));
        }
        return Promise.resolve();
    };

    function extractCNPJ(chaveAcesso: string) {
        if (chaveAcesso.length !== 44) {
            message.error("A chave de acesso deve ter 44 dígitos.");
        }
        return chaveAcesso.substring(6, 20);
    }

    function extractNumeroNF(chaveAcesso: string) {
        if (chaveAcesso.length !== 44) {
            message.error("A chave de acesso deve ter 44 dígitos.");
        }
        return chaveAcesso.substring(25, 34);
    }

    const anexosCif = analisarAnexosAgendamento(cifFiles);
    const anexosFob = analisarAnexosAgendamento(fobFiles);
    const isCifFile = anexosCif.valido;
    const isFobFile = anexosFob.valido;
    const descricaoTipoFrete = isFob
        ? <span>Adicionar NF do Conhecimento de Frete <strong>(FOB)</strong></span>
        : <span>Frete por conta do Emitente <strong>(CIF)</strong></span>;
    const isNotTributum = anexosCif.temXml || anexosFob.temXml;
    const tipoFrete: TipoFrete = isFob ? 'FOB' : 'CIF';

    const chaveNfeAtual = String(chaveNfe ?? '');
    const ufFilialSelecionada = buscarUfDaFilial(filial);
    const isFilialSemUf = !ufFilialSelecionada;
    const ufDestinatarioNf = destinatarioNf?.chaveNfe === chaveNfeAtual ? destinatarioNf.uf : null;
    const estadoConferenciaUf = ufFilialSelecionada ? classificarConferenciaUf(ufDestinatarioNf, ufFilialSelecionada) : null;
    const isDestinatarioConferido = ufDestinatarioNf !== null;
    const isDestinatarioDivergente = estadoConferenciaUf === 'CONFLITO';
    const isChaveNfePreenchida = chaveNfeAtual.length > 0;
    const isFilialConfirmadaPeloUsuario = chaveNfeFilialConfirmada !== null && chaveNfeFilialConfirmada === chaveNfeAtual;
    const isConfirmacaoFilialPendente = !isDestinatarioConferido && !isFilialConfirmadaPeloUsuario;
    const isCienciaUfDivergenteRegistrada = isDestinatarioDivergente
        && cienciaUfDivergente?.chaveNfe === chaveNfeAtual
        && cienciaUfDivergente.uf === ufDestinatarioNf;
    const isCienciaUfDivergentePendente = isDestinatarioDivergente && !isCienciaUfDivergenteRegistrada;
    const isAdicionarNfBloqueado = isFilialSemUf || isCienciaUfDivergentePendente || (isChaveNfePreenchida && isConfirmacaoFilialPendente);
    const filialDoDestinatario = buscarFilialDaUf(ufDestinatarioNf);
    const isDestinatarioDeFilialConhecida = Boolean(filialDoDestinatario);
    const origemXmlNotaFiscal = cifFiles.find(isArquivoXml)?.name ?? 'XML obtido pela busca automática';
    const situacaoXmlDivergente = isCienciaUfDivergenteRegistrada ? 'ciência registrada' : 'aguardando sua ciência';

    const hasNotasNaLista = formRequestNotasDto.length > 0;
    const hasBlocosCifFob = formRequestGeralDto.length > 0;
    const notasEmBlocos = formRequestGeralDto.flatMap(bloco => bloco.notas ?? []);
    const totalNotas = formRequestNotasDto.length + notasEmBlocos.length;
    const totalVolumes = somarVolumes(formRequestNotasDto) + somarVolumes(notasEmBlocos);
    const totalReservadas = contarMarcas(conferenciasPorChave, 'RESERVADA_SEM_XML');
    const totalComCienciaUf = contarMarcas(conferenciasPorChave, 'CIENCIA_UF_DIVERGENTE');
    const totalConferidas = formRequestNotasDto.length - totalReservadas - totalComCienciaUf;
    const totalAnexos = cifFiles.length + fobFiles.length;
    const isFinalizarBloqueado = !hasBlocosCifFob;
    const quantidadePdfNf = contarPdf(cifFiles);
    const quantidadeXmlNf = contarXml(cifFiles);
    const quantidadePdfConhecimento = contarPdf(fobFiles);
    const quantidadeXmlConhecimento = contarXml(fobFiles);
    const isDestinatarioConfere = estadoConferenciaUf === 'VALIDADO';
    const isRegraDestinatarioAtendida = isDestinatarioConfere || isCienciaUfDivergenteRegistrada;
    const isXmlConhecimentoUnico = quantidadeXmlConhecimento === 1;
    const isPdfConhecimentoUnico = quantidadePdfConhecimento === 1;
    const isPdfNfUnico = quantidadePdfNf === 1;
    const isQuantidadeAnexosNfValida = cifFiles.length > 0 && cifFiles.length <= 2;
    const isXmlNfDentroDoLimite = quantidadeXmlNf <= 1;

    const resolverEtapaAtual = () => {
        if (hasBlocosCifFob) return 2;
        if (hasNotasNaLista) return 1;
        return 0;
    };
    const etapaAtual = resolverEtapaAtual();

    const resolverMotivoBloqueioNf = () => {
        if (isFilialSemUf) return 'Selecione a filial novamente na tela de agendamentos para liberar Adicionar NF.';
        if (isDestinatarioDivergente) return `O XML anexado é de outra UF (${ufDestinatarioNf}): marque a ciência no aviso para liberar Adicionar NF.`;
        return `Confirme acima que esta NF é destinada à filial ${ufFilialSelecionada} para liberar Adicionar NF.`;
    };

    const resolverMarcaDaNota = (chaveNfe?: string) => conferenciasPorChave[chaveNfe ?? '']?.marca ?? 'CONFERIDA';

    const resolverUfDivergenteDaNota = (chaveNfe?: string) => conferenciasPorChave[chaveNfe ?? '']?.ufDestinatario ?? '';

    const resolverClasseLinhaNota = (chaveNfe?: string) => {
        switch (resolverMarcaDaNota(chaveNfe)) {
            case 'RESERVADA_SEM_XML':
                return 'linha-reservada';
            case 'CIENCIA_UF_DIVERGENTE':
                return 'linha-divergente';
            case 'CONFERIDA':
                return '';
        }
    };

    const resolverDicaListaNotas = () => {
        if (!hasNotasNaLista) return 'Nenhuma nota adicionada';
        const volumes = `${somarVolumes(formRequestNotasDto)} volumes`;
        const marcas = [
            totalConferidas > 0 && `${totalConferidas} ${pluralizar(totalConferidas, 'conferida', 'conferidas')} pelo XML`,
            totalReservadas > 0 && `${totalReservadas} ${pluralizar(totalReservadas, 'reservada', 'reservadas')} sob sua confirmação`,
            totalComCienciaUf > 0 && `${totalComCienciaUf} ${pluralizar(totalComCienciaUf, 'recebida', 'recebidas')} com ciência de UF divergente`,
        ].filter(Boolean).join(', ');
        return `${formRequestNotasDto.length} ${pluralizar(formRequestNotasDto.length, 'nota', 'notas')} · ${volumes} · ${marcas} — ainda não agrupadas em um bloco`;
    };

    const preencherNotaFiscalPeloXml = async (arquivo: Blob) => {
        const xml = await lerTextoDoArquivo(arquivo);
        const nota = xml ? lerNotaFiscalDoXml(xml) : null;
        if (!nota) return;

        form.setFieldsValue({
            chaveNfe: nota.chaveNfe ?? undefined,
            razaoSocialFornecedor: nota.razaoSocialEmitente ?? undefined,
            dataEmissao: nota.dataEmissao ? dayjs(nota.dataEmissao) : undefined,
            volumeCarga: nota.volumeCarga ?? undefined,
        });

        setDestinatarioNf(nota.chaveNfe && nota.ufDestinatario ? {chaveNfe: nota.chaveNfe, uf: nota.ufDestinatario} : null);
        setCienciaUfDivergente(null);
    };

    const preencherConhecimentoPeloXml = async (arquivo: Blob) => {
        const xml = await lerTextoDoArquivo(arquivo);
        const numeroConhecimento = xml ? lerNumeroConhecimentoDoXml(xml) : null;
        if (numeroConhecimento !== null) {
            form.setFieldsValue({numeroConhecimento});
        }
    };

    const handleNfeGetTributum = async () => {
        setButtonTributumLoading(true)
        try {
            const chaveNfe = form.getFieldValue('chaveNfe');
            if (!chaveNfe) return;
            const data = await buscarNfeTributum(chaveNfe);
            if (!data.infNFe) throw new Error('Resposta do Tributum sem dados da NFe.');
            const volumes = data.infNFe.transp.vol;
            const volume = Array.isArray(volumes) ? volumes[0]?.qVol : volumes?.qVol;
            const ufDestinatario = data.infNFe.dest?.enderDest?.UF;
            form.setFieldsValue({razaoSocialFornecedor: data.infNFe.emit.xNome ?? ''});
            form.setFieldsValue({dataEmissao: dayjs(data.infNFe.ide.dhEmi)});
            form.setFieldsValue({volumeCarga: volume});
            setDestinatarioNf(ufDestinatario ? {chaveNfe, uf: ufDestinatario} : null);
            setCienciaUfDivergente(null);
        } catch {
            message.error("Erro ao buscar os dados do Tributum!")
        } finally {
            setButtonTributumLoading(false)
        }
    };

    const handleFileUploadForCIF = (file: ArquivoUpload) => {
        setCifFiles((prevFiles) => [...prevFiles, file]);
        if (!isArquivoXml(file)) return;
        void preencherNotaFiscalPeloXml(file);
    };

    const handleFileUploadForFOB = (file: ArquivoUpload) => {
        setFobFiles((prevFiles) => [...prevFiles, file]);
        if (!isArquivoXml(file)) return;
        void preencherConhecimentoPeloXml(file);
    };

    const handleFileDeleteForCIF = (fileToDelete: {uid: string}) => {
        const updatedFiles = (files: ArquivoUpload[]) => files.filter(file => file.uid !== fileToDelete.uid);

        setCifFiles(updatedFiles);
        cleanFormNotFob();

    };

    const handleFileDeleteForFOB = (fileToDelete: {uid: string}) => {
        const updatedFiles = (files: ArquivoUpload[]) => files.filter(file => file.uid !== fileToDelete.uid);

        setFobFiles(updatedFiles);
        cleanForm();
    };

    const limparConferenciaDestinatario = () => {
        setDestinatarioNf(null);
        setChaveNfeFilialConfirmada(null);
        setCienciaUfDivergente(null);
    }

    const cleanForm = () => {
        setCifFiles([]);
        setFobFiles([]);
        NFUploadRef.current?.clearFiles();
        CTOUploadRef.current?.clearFiles();
        limparConferenciaDestinatario();
    }

    const handleTipoFreteChange = (novoTipoFrete: TipoFrete) => {
        const novoIsFob = novoTipoFrete === 'FOB';
        setIsFob(novoIsFob);
        cleanForm();
        setFormRequestNotasDto([]);
        setConferenciasPorChave({});
        if (!novoIsFob) form.setFieldsValue({numeroConhecimento: undefined});
    };

    const cleanFormNotFob = () => {
        const saveFields = {
            numeroConhecimento: form.getFieldValue('numeroConhecimento'),
        };
        form.resetFields();
        form.setFieldsValue(saveFields);
        setCifFiles([]);
        NFUploadRef.current?.clearFiles();
        limparConferenciaDestinatario();
    }

    const handleTrocarFilial = () => navigate('/');

    const handleAgendarNaFilialDoDestinatario = () => {
        if (!filialDoDestinatario) return;
        definirFilial(filialDoDestinatario.value);
        navigate('/');
    };

    const handleTrocarXmlNotaFiscal = () => cleanFormNotFob();

    const handleConfirmacaoFilialChange = (evento: CheckboxChangeEvent) => {
        setChaveNfeFilialConfirmada(evento.target.checked ? chaveNfeAtual : null);
    };

    const handleCienciaUfDivergenteChange = (evento: CheckboxChangeEvent) => {
        setCienciaUfDivergente(evento.target.checked && ufDestinatarioNf ? {chaveNfe: chaveNfeAtual, uf: ufDestinatarioNf} : null);
    };

    const handleAnexarXmlNotaReservada = (chaveNfe?: string) => {
        if (!chaveNfe) return;
        setChaveNotaEmConferencia(chaveNfe);
        inputXmlConferenciaRef.current?.click();
    };

    const handleXmlConferenciaChange = async (evento: ChangeEvent<HTMLInputElement>) => {
        const arquivo = evento.target.files?.[0];
        const chaveNota = chaveNotaEmConferencia;
        evento.target.value = '';
        setChaveNotaEmConferencia(null);
        if (!arquivo || !chaveNota) return;

        if (isFilialSemUf) {
            message.error(MENSAGEM_FILIAL_SEM_UF);
            return;
        }

        const destinatario = await lerDestinatarioDoArquivoXml(arquivo);

        if (!destinatario) {
            message.error('Não foi possível ler o destinatário no XML selecionado.');
            return;
        }

        if (destinatario.chaveNfe !== chaveNota) {
            message.error('O XML selecionado é de outra Nota Fiscal.');
            return;
        }

        if (destinatario.uf !== ufFilialSelecionada) {
            message.warning(`O destinatário do XML é de ${destinatario.uf} e a filial selecionada é ${ufFilialSelecionada}. Para registrar a ciência, exclua a nota e adicione-a de novo com este XML.`);
            return;
        }

        try {
            const arquivosDto = await mapFilesToDto([arquivo]);

            setFormRequestNotasDto(prevState => prevState.map(nota => nota.chaveNfe === chaveNota
                ? {...nota, cifFilesDto: [...(nota.cifFilesDto ?? []), ...arquivosDto]}
                : nota));
            setConferenciasPorChave(prevState => ({...prevState, [chaveNota]: {marca: 'CONFERIDA', ufDestinatario: destinatario.uf}}));
            message.success('Nota Fiscal conferida pelo XML.');
        } catch {
            message.error('Não foi possível anexar o XML selecionado.');
        }
    };

    const onFinish = async (values: FinalizarFormValues) => {
        if (isFilialSemUf) {
            message.error(MENSAGEM_FILIAL_SEM_UF);
            return;
        }

        if (!estadoConferenciaUf) return;

        if (isCienciaUfDivergentePendente) {
            message.warning(`Marque a ciência de que esta Nota Fiscal é de ${ufDestinatarioNf} antes de adicioná-la na filial ${ufFilialSelecionada}.`);
            return;
        }

        if (isConfirmacaoFilialPendente) {
            message.warning(`Confirme que esta Nota Fiscal é destinada à filial ${ufFilialSelecionada} antes de adicioná-la.`);
            return;
        }

        const marcaConferencia = resolverMarcaConferenciaNota(estadoConferenciaUf);
        const fobFileDto = isFobFile ? await mapFilesToDto(fobFiles) : [];
        const cifFileDto = isCifFile ? await mapFilesToDto(cifFiles) : [];
        const isPdfFileCif = cifFileDto.filter(file => file.type === 'application/pdf').length !== 1;
        const isPdfFileFob = fobFileDto.filter(file => file.type === 'application/pdf').length !== 1;

        if (!isFob && (cifFileDto.length < 1 || fobFileDto.length !== 0)) {
            message.warning('Por conta do Emitente (CIF) é permitido somente 1 PDF e 1 XML');
            return;
        }

        if (isFob && (isPdfFileCif || isPdfFileFob)) {
            message.warning("Deve conter DOIS PDF. (NF, CTe)");
            return;
        }

        if (!isFob && isPdfFileCif) {
            message.warning("Deve conter UM PDF. (NF)");
            return;
        }

        const numeroConhecimento = converterNumeroConhecimento(values.numeroConhecimento);

        setFormRequestFobDto({
            tipoConhecimento: isFob ? "FOB" : "CIF",
            numeroConhecimento,
            fobFilesDto: fobFileDto,
        });


        const updatedCifRequestDto = {
            chaveNfe: values.chaveNfe,
            numeroNota: Number(extractNumeroNF(values.chaveNfe ?? '')),
            cnpjFornecedor: extractCNPJ(values.chaveNfe ?? ''),
            dataEmissao: values.dataEmissao?.format('YYYY-MM-DDTHH:mm:ss'),
            razaoSocialFornecedor: values.razaoSocialFornecedor,
            tipoConhecimento: isFob ? "FOB" : "CIF",
            volumeCarga: Number(values.volumeCarga),
            numeroConhecimento,
            cifFilesDto: cifFileDto,
        };

        try {
            setFormRequestNotasDto(prevState => [...prevState, updatedCifRequestDto]);
            const chaveNfeAdicionada = values.chaveNfe;
            if (chaveNfeAdicionada) {
                setConferenciasPorChave(prevState => ({...prevState, [chaveNfeAdicionada]: {marca: marcaConferencia, ufDestinatario: ufDestinatarioNf}}));
            }
            message.success("Dados adicionados à lista com sucesso");
            cleanFormNotFob();
            setCifFiles([]);
            NFUploadRef.current?.clearFiles();
        } catch {
            message.error("Erro ao finalizar o agendamento");
        }
    }

    const handleNotaFiscalDelete = (chaveNfe?: string) => {
        setFormRequestNotasDto((prevData) => prevData.filter(item => item.chaveNfe !== chaveNfe));
        setConferenciasPorChave((prevData) => Object.fromEntries(Object.entries(prevData).filter(([chave]) => chave !== chaveNfe)));
    };

    const handleGeralAdd = async () => {
        if (!formRequestFobDto) return;
        if (formRequestNotasDto.length < 1) {
            message.warning("Deve conter pelo menos 1 Nota");
            return
        }

        const hasFOBInNumeroConhecimentoEqual = formRequestGeralDto.some(request =>
            String(request.numeroConhecimento).trim() === String(formRequestFobDto.numeroConhecimento).trim()
        );

        if (formRequestFobDto.tipoConhecimento === 'FOB' && hasFOBInNumeroConhecimentoEqual) {
            message.warning("Conhecimento já adicionado");
            return;
        }


        const updatedGeralRequestDto = {...formRequestFobDto, notas: formRequestNotasDto}
        setFormRequestGeralDto(prevState => [...prevState, updatedGeralRequestDto]);
        setFormRequestNotasDto([]);
        setConferenciasPorChave({});

        cleanForm();
    };

    const handleGeralDelete = (numeroConhecimento?: number) => {
        setFormRequestGeralDto((prevData) => prevData.filter(item => item.numeroConhecimento !== numeroConhecimento));
    };

    const handleGeralFinish = async () => {
        if (formRequestGeralDto.length < 1) {
            message.warning("Deve conter pelo menos 1 Nota");
            return
        }
        const volumeTotalCarga = somarVolumes(notasEmBlocos)
        const updatedRequestDto = {agendaId: agendaId, volumeCargaTotal: volumeTotalCarga, agendas: formRequestGeralDto}

        try {
            await concluirMutation.mutateAsync(updatedRequestDto);
            message.success("Cadastro finalizado com sucesso!!")
            navigate(`/`);
        } catch (error) {
            message.error(resolverMensagemErro(error, "Ocorreu um erro ao realizar o cadastro"))
            navigate(`/`);
        }
    };

    const columnsListNFUpload: TableColumnsType<AgendaExternaCifDto> = [
        {
            title: 'Chave de Acesso NF',
            dataIndex: 'chaveNfe',
            key: 'chaveNfe',
        },
        {
            title: 'Número da Nota',
            dataIndex: 'numeroNota',
            key: 'numeroNota',
        },
        {
            title: 'Total de Volumes',
            dataIndex: 'volumeCarga',
            key: 'volumeCarga',
        },
        {
            title: 'Data da Emissão',
            dataIndex: 'dataEmissao',
            key: 'dataEmissao',
            render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : ''
        },
        {
            title: 'Destinatário',
            key: 'destinatario',
            render: (_, record) => renderMarcaConferencia(record.chaveNfe),
        },
        {
            title: 'Ações',
            key: 'actionsNotaFiscal',
            align: 'right',
            render: (_, record) => (
                <Flex align={'center'} justify={'flex-end'} gap={8} wrap={true}>
                    {resolverMarcaDaNota(record.chaveNfe) === 'RESERVADA_SEM_XML' && (
                        <Tooltip title="Anexar o XML converte para conferida sem refazer os dados">
                            <Button icon={<UploadOutlined/>} type="primary" size="small"
                                    onClick={() => handleAnexarXmlNotaReservada(record.chaveNfe)}>
                                Anexar XML
                            </Button>
                        </Tooltip>
                    )}
                    <Popconfirm
                        title="Tem certeza de que deseja excluir?"
                        description={`A nota ${record.numeroNota ?? ''} sai da lista. Os anexos e os demais campos continuam como estão.`}
                        onConfirm={() => handleNotaFiscalDelete(record.chaveNfe)}
                        okText="Sim"
                        cancelText="Não"
                    >
                        <Button icon={<DeleteOutlined className={"delete-outline-icon-class"}/>} size="small"
                                aria-label={`Excluir a nota ${record.numeroNota ?? ''} da lista`}>
                            Excluir
                        </Button>
                    </Popconfirm>
                </Flex>
            ),
        },
    ]

    const columnsListNotasUpload: TableColumnsType<AgendaExternaFobDto> = [
        {
            title: 'Número de Conhecimento',
            dataIndex: 'numeroConhecimento',
            key: 'numeroConhecimento',
        },
        {
            title: 'Número da Nota',
            key: 'numeroNota',
            render: (_, record) => {
                return record.notas && record.notas.length > 0
                    ? record.notas.map(nota => nota.numeroNota).join(', ')
                    : '-';
            }
        },
        {
            title: 'Tipo de Conhecimento',
            dataIndex: 'tipoConhecimento',
            key: 'tipoConhecimento',
        },
        {
            title: 'Ações',
            key: 'actionsCifFob',
            align: 'right',
            render: (_, record) => (
                <Popconfirm
                    title="Tem certeza de que deseja excluir?"
                    onConfirm={() => handleGeralDelete(record.numeroConhecimento)}
                    okText="Sim"
                    cancelText="Não"
                >
                    <Button icon={<DeleteOutlined className={"delete-outline-icon-class"}/>} size="small"
                            aria-label={`Excluir o bloco ${record.tipoConhecimento ?? ''} ${record.numeroConhecimento ?? ''} da lista`}>
                        Excluir
                    </Button>
                </Popconfirm>
            ),
        },
    ]

    const etapas = [
        {
            titulo: 'Notas fiscais',
            contagem: totalNotas > 0 ? `${totalNotas} ${pluralizar(totalNotas, 'adicionada', 'adicionadas')}` : null,
            isConcluida: totalNotas > 0,
        },
        {
            titulo: 'Bloco CIF/FOB',
            contagem: hasBlocosCifFob ? `${formRequestGeralDto.length} ${pluralizar(formRequestGeralDto.length, 'adicionado', 'adicionados')}` : 'pendente',
            isConcluida: hasBlocosCifFob,
        },
        {titulo: 'Finalizar Agendamento', contagem: null, isConcluida: false},
    ];

    const renderStepBar = () => (
        <nav className="step-bar" aria-label="Etapas para finalizar o pré-agendamento">
            {etapas.map((etapa, indice) => {
                const isEtapaAtual = indice === etapaAtual;
                const classeChip = [
                    'step-bar__chip',
                    isEtapaAtual && 'step-bar__chip--atual',
                    etapa.isConcluida && 'step-bar__chip--concluida',
                ].filter(Boolean).join(' ');
                return (
                    <Fragment key={etapa.titulo}>
                        {indice > 0 && <span className="step-bar__separador" aria-hidden="true">›</span>}
                        <span className={classeChip} aria-current={isEtapaAtual ? 'step' : undefined}>
                            <span className="step-bar__indice">{etapa.isConcluida ? <CheckOutlined/> : indice + 1}</span>
                            {etapa.titulo}
                            {etapa.contagem && <span className="step-bar__contagem">{etapa.contagem}</span>}
                        </span>
                    </Fragment>
                );
            })}
        </nav>
    );

    const renderRegra = (isOk: boolean, texto: ReactNode) => (
        <li className={isOk ? 'regras__item regras__item--ok' : 'regras__item regras__item--pendente'}>
            {isOk ? <CheckCircleOutlined/> : <MinusCircleOutlined/>}
            <span>{texto}</span>
        </li>
    );

    const renderResumoPreAgendamento = () => {
        const resumo = resumoQuery.data;
        const entrega = resumo?.dataEntrega ? dayjs(resumo.dataEntrega).format('DD/MM/YYYY · HH:mm') : null;
        return (
            <section className="section-card section-card--resumo" aria-label="Pré-agendamento que está sendo finalizado">
                <div className="resumo-agenda">
                    <div className="resumo-agenda__grade">
                        <div className="resumo-agenda__head">
                            <span className="resumo-agenda__eyebrow">Pré-agendamento</span>
                            <span className="resumo-agenda__codigo">Nº {agendaId}</span>
                        </div>
                        <div className="resumo-agenda__item">
                            <span className="resumo-agenda__label">Filial</span>
                            <span className="resumo-agenda__valor">{ufFilialSelecionada ?? 'não identificada'}</span>
                        </div>
                        {entrega && (
                            <div className="resumo-agenda__item">
                                <span className="resumo-agenda__label">Entrega</span>
                                <span className="resumo-agenda__valor">{entrega}</span>
                            </div>
                        )}
                        {resumo?.tipoCaminhao && (
                            <div className="resumo-agenda__item">
                                <span className="resumo-agenda__label">Tipo de caminhão</span>
                                <span className="resumo-agenda__valor">{resumo.tipoCaminhao}</span>
                            </div>
                        )}
                        {resumo?.status && (
                            <div className="resumo-agenda__item">
                                <span className="resumo-agenda__label">Status</span>
                                <span className="resumo-agenda__valor">{resumo.status}</span>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        );
    };

    const renderMarcaConferencia = (chaveNfe?: string) => {
        switch (resolverMarcaDaNota(chaveNfe)) {
            case 'RESERVADA_SEM_XML':
                return (
                    <Flex vertical={true} align={'flex-start'} gap={4}>
                        <Tag color="warning" icon={<ExclamationCircleOutlined/>}>
                            {ufFilialSelecionada} sob sua confirmação
                        </Tag>
                        <Text type="secondary">Sem XML — destinatário não conferido</Text>
                    </Flex>
                );
            case 'CIENCIA_UF_DIVERGENTE':
                return (
                    <Flex vertical={true} align={'flex-start'} gap={4}>
                        <Tag color="warning" variant="outlined" icon={<SwapOutlined/>}>
                            NF de {resolverUfDivergenteDaNota(chaveNfe)} · ciência registrada
                        </Tag>
                        <Text type="secondary">Recebida na filial {ufFilialSelecionada} e transportada até {resolverUfDivergenteDaNota(chaveNfe)}</Text>
                    </Flex>
                );
            case 'CONFERIDA':
                return (
                    <Tag color="success" icon={<CheckCircleOutlined/>}>
                        {ufFilialSelecionada} confere
                    </Tag>
                );
        }
    };

    const renderConfirmacaoFilialSemXml = () => {
        const isConfirmacaoNecessaria = !isFilialSemUf && !isDestinatarioConferido && isChaveNfePreenchida;
        if (!isConfirmacaoNecessaria) return null;
        return (
            <Alert
                type="warning"
                showIcon={true}
                title="Sem o XML, quem responde pela filial é você"
                description={
                    <Flex vertical={true} align={'flex-start'} gap={8}>
                        <Text>A chave de acesso sozinha não informa o destinatário. Anexe o XML ao lado ou use a lupa para conferir automaticamente — ou confirme abaixo e siga.</Text>
                        <Checkbox checked={isFilialConfirmadaPeloUsuario} onChange={handleConfirmacaoFilialChange}>
                            Confirmo que esta NF é destinada à filial <Text strong={true}>{ufFilialSelecionada}</Text>.
                        </Checkbox>
                        <Text type="secondary">Ao confirmar, <strong>Adicionar NF</strong> é liberado e a nota entra na lista marcada como reservada. Se a filial estiver errada, a carga é recusada na portaria e o agendamento precisa ser refeito.</Text>
                    </Flex>
                }
            />
        );
    };

    const renderConferenciaDestinatario = () => (
        <div className="nf-check-stack">
            {isFilialSemUf && (
                <Alert
                    type="error"
                    role="alert"
                    showIcon={true}
                    title="Não foi possível determinar a UF da filial selecionada"
                    description={
                        <Flex vertical={true} align={'flex-start'} gap={8}>
                            <Text>{MENSAGEM_FILIAL_SEM_UF}</Text>
                            <Text type="secondary">Volte para a tela de agendamentos e selecione a filial novamente.</Text>
                        </Flex>
                    }
                    action={<Button icon={<SwapOutlined/>} onClick={handleTrocarFilial}>Trocar filial</Button>}
                />
            )}
            {isDestinatarioDivergente && (
                <>
                    <span className="nf-file">
                        <FileTextOutlined aria-hidden="true"/>
                        <span>{origemXmlNotaFiscal} — <strong className="nf-file__situacao">{situacaoXmlDivergente}</strong></span>
                    </span>
                    <div className="ciencia-uf" role="group" aria-labelledby="ciencia-uf-titulo">
                        <WarningOutlined className="ciencia-uf__icone" aria-hidden="true"/>
                        <div className="ciencia-uf__corpo">
                            <h3 className="ciencia-uf__titulo" id="ciencia-uf-titulo">A UF desta NF não é a da filial selecionada</h3>
                            <p className="ciencia-uf__lead">
                                Em alguns casos a nota é recebida na filial <strong>{ufFilialSelecionada}</strong> e a carga é transportada depois até o destinatário em <strong>{ufDestinatarioNf}</strong>. Se este for o caso, registre sua ciência abaixo para adicionar a NF.
                            </p>
                            <Flex gap={8} wrap={true}>
                                <Tag>Filial selecionada: <Text strong={true}>{ufFilialSelecionada}</Text></Tag>
                                <Tag>Destinatário da NF: <Text strong={true}>{ufDestinatarioNf}</Text></Tag>
                            </Flex>
                            <div className="ciencia-uf__confirmacao">
                                <Checkbox checked={isCienciaUfDivergenteRegistrada} onChange={handleCienciaUfDivergenteChange}>
                                    <span className="ciencia-uf__rotulo">
                                        Estou ciente de que esta NF é de <strong>{ufDestinatarioNf}</strong> e quero agendar na filial <strong>{ufFilialSelecionada}</strong> mesmo assim.
                                    </span>
                                </Checkbox>
                            </div>
                            <Text type="secondary">Ao marcar, <strong>Adicionar NF</strong> é liberado e a nota entra na lista marcada como recebida com ciência de UF divergente.</Text>
                            <div className="ciencia-uf__alternativas">
                                <span className="ciencia-uf__alternativas-rotulo">Se foi engano:</span>
                                <Tooltip title={isDestinatarioDeFilialConhecida ? undefined : `Não há filial Diferpan em ${ufDestinatarioNf}. Troque a filial na tela de agendamentos.`}>
                                    <Button icon={<SwapOutlined/>} onClick={handleAgendarNaFilialDoDestinatario} disabled={!isDestinatarioDeFilialConhecida}>
                                        Agendar na filial {ufDestinatarioNf}
                                    </Button>
                                </Tooltip>
                                <Button icon={<UploadOutlined/>} onClick={handleTrocarXmlNotaFiscal}>
                                    Trocar o XML
                                </Button>
                                <Text type="secondary">Somente os campos e anexos desta nota são limpos — as notas já adicionadas e o restante da tela continuam como estão.</Text>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {isDestinatarioConfere && (
                <div>
                    <Tag color="success" icon={<CheckCircleOutlined/>}>
                        Destinatário do XML confere com a filial {ufFilialSelecionada}
                    </Tag>
                </div>
            )}
        </div>
    );

    const opcoesTipoFrete: SegmentedProps<TipoFrete>['options'] = [
        {
            value: 'CIF',
            label: (
                <span className="frete-segmentado__opcao">
                    <span className="frete-segmentado__nome">CIF{!isFob && <CheckCircleOutlined/>}</span>
                    <span className="frete-segmentado__descricao">Frete por conta do Emitente</span>
                </span>
            ),
        },
        {
            value: 'FOB',
            label: (
                <span className="frete-segmentado__opcao">
                    <span className="frete-segmentado__nome">FOB{isFob && <CheckCircleOutlined/>}</span>
                    <span className="frete-segmentado__descricao">Adicionar NF do Conhecimento de Frete</span>
                </span>
            ),
        },
    ];

    return (
        <div className="fluxo-agendamento finalizar-agendamento">
            {renderStepBar()}
            {renderResumoPreAgendamento()}

            <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={renderMarcadorObrigatorio}>
                <Flex vertical={true} gap={16}>
                    <section className="section-card" aria-label="Dados para o Agendamento">
                        <div className="section-card__head">
                            <h2 className="section-card__title">Dados para o Agendamento</h2>
                            <span className="section-card__hint">Quem paga o frete define quais anexos são exigidos</span>
                        </div>
                        <div className="frete-segmentado">
                            <Segmented<TipoFrete>
                                className="finalizar-agendamento__segmentado"
                                options={opcoesTipoFrete}
                                value={tipoFrete}
                                onChange={handleTipoFreteChange}
                                aria-label="Tipo de frete"
                            />
                            {hasNotasNaLista && (
                                <p className="aviso aviso--alerta">
                                    <ExclamationCircleOutlined/>
                                    <span>
                                        Trocar para <strong>{isFob ? 'CIF' : 'FOB'}</strong> descarta {formRequestNotasDto.length === 1
                                            ? 'a nota já adicionada'
                                            : `as ${formRequestNotasDto.length} notas já adicionadas`} e os anexos desta tela.
                                    </span>
                                </p>
                            )}
                        </div>
                    </section>

                    {isFob && (
                        <section className="section-card" aria-label="Dados do Conhecimento de Frete">
                            <div className="section-card__head">
                                <h2 className="section-card__title">Preencha os dados do Conhecimento de Frete</h2>
                                <span className="section-card__hint">Exigido porque o frete é FOB</span>
                            </div>
                            <div className="section-card__body">
                                <div className="section-card__coluna">
                                    <UploadPdfOuXml
                                        ref={CTOUploadRef}
                                        onUpload={handleFileUploadForFOB}
                                        onDelete={handleFileDeleteForFOB}
                                        maxCount={2}
                                        disabled={!isFob}
                                        infoText={"Arquivos XML e PDF do C. de Frete (FOB)"}
                                        className="upload-area"
                                    />
                                    <ul className="regras">
                                        {renderRegra(isXmlConhecimentoUnico, `${quantidadeXmlConhecimento} XML do conhecimento anexado`)}
                                        {renderRegra(isPdfConhecimentoUnico, `${quantidadePdfConhecimento} PDF do conhecimento anexado — máximo de 2 arquivos`)}
                                    </ul>
                                    <p className="aviso aviso--info">
                                        <InfoCircleOutlined/>
                                        <span>Remover um anexo não apaga os dados já preenchidos nos campos abaixo.</span>
                                    </p>
                                </div>
                                <div className="form-field">
                                    <Form.Item name="numeroConhecimento" label="Número conhecimento" extra="Somente números" required={true}
                                               normalize={normalizarSomenteDigitos}
                                               rules={[{validator: validateNumeroConhecimento}]}>
                                        <Input placeholder="Número conhecimento" inputMode="numeric" disabled={!isFob}/>
                                    </Form.Item>
                                    <p className="form-field__nota">Lido do XML anexado. Um mesmo conhecimento só pode entrar uma vez na lista de CIF/FOB.</p>
                                </div>
                            </div>
                        </section>
                    )}

                    <section className="section-card" aria-label="Dados da Nota Fiscal">
                        <div className="section-card__head">
                            <h2 className="section-card__title">Preencha os dados da Nota Fiscal</h2>
                            <div className="section-card__aside">
                                <span className="section-card__hint">{descricaoTipoFrete}</span>
                                <span className="filial-tag">
                                    <ShopOutlined/> Filial selecionada: <strong className="filial-tag__uf">{ufFilialSelecionada ?? 'não identificada'}</strong>
                                </span>
                                <Button type="link" size="small" onClick={handleTrocarFilial}>Trocar filial</Button>
                            </div>
                        </div>
                        <div className="section-card__body">
                            <div className="section-card__coluna">
                                <UploadPdfOuXml
                                    ref={NFUploadRef}
                                    onUpload={handleFileUploadForCIF}
                                    onDelete={handleFileDeleteForCIF}
                                    maxCount={2}
                                    infoText={"Arquivos XML e PDF da Nota Fiscal  (CIF)"}
                                    className="upload-area"
                                />
                                {renderConferenciaDestinatario()}
                                <ul className="regras">
                                    {renderRegra(isPdfNfUnico, isFob ? 'Deve conter DOIS PDF. (NF, CTe)' : 'Deve conter UM PDF. (NF)')}
                                    {renderRegra(isQuantidadeAnexosNfValida, 'Número máximo de arquivos: 2')}
                                    {renderRegra(isXmlNfDentroDoLimite, 'Formatos aceitos: .pdf, .xml')}
                                    {renderRegra(isRegraDestinatarioAtendida, <>Destinatário do XML na filial <strong>{ufFilialSelecionada ?? 'selecionada'}</strong> — outra UF exige sua ciência</>)}
                                </ul>
                                <p className="aviso aviso--info">
                                    <ThunderboltOutlined/>
                                    <span>Anexe o XML da nota para preencher chave, razão social, emissão e volume automaticamente.</span>
                                </p>
                            </div>

                            <div className="form-field">
                                <div className="form-field__compacto">
                                    <Form.Item name="chaveNfe" label="Chave de acesso NF" extra={`${String(chaveNfe ?? "").length}/44`}
                                               normalize={normalizarSomenteDigitos}
                                               rules={[
                                                   {required: true, message: 'Campo obrigatório!'},
                                                   {
                                                       len: 44,
                                                       message: 'O campo deve conter 44 dígitos!',
                                                   },
                                               ]}>
                                        <Input placeholder="Chave de acesso NF" inputMode="numeric" maxLength={44}/>
                                    </Form.Item>
                                    <Tooltip title="Pesquisa automática pela chave de acesso">
                                        <Button disabled={isNotTributum} onClick={handleNfeGetTributum}
                                                loading={buttonTributumLoading} icon={<SearchOutlined/>}
                                                className="form-field__botao-busca" type="primary"/>
                                    </Tooltip>
                                </div>
                                <p className="form-field__nota">A lupa busca o XML da nota pela chave de acesso. É esse XML que traz o destinatário para conferir com a filial <strong>{ufFilialSelecionada ?? 'selecionada'}</strong>.</p>
                                {renderConfirmacaoFilialSemXml()}
                            </div>
                            <div className="form-field">
                                <Form.Item name="razaoSocialFornecedor" label="Razão social"
                                           rules={[{required: true, message: 'Campo obrigatório!'}]}>
                                    <Input placeholder="Digite a razão social"/>
                                </Form.Item>
                            </div>
                            <div className="form-field">
                                <Form.Item name="dataEmissao" label="Data de emissão" rules={[{required: true, message: 'Campo obrigatório!'}]}>
                                    <DatePicker placeholder="Data de emissão" format={FORMATO_DATA}/>
                                </Form.Item>
                            </div>
                            <div className="form-field">
                                <Form.Item name="volumeCarga" label="Volume total da carga" normalize={normalizarSomenteDigitos}
                                           rules={[{required: true, message: 'Campo obrigatório!'}]}>
                                    <Input placeholder="Volume total da carga" inputMode="numeric"/>
                                </Form.Item>
                            </div>
                        </div>
                        <div className="section-card__actions">
                            {isAdicionarNfBloqueado && <span className="section-card__actions-note">{resolverMotivoBloqueioNf()}</span>}
                            <Button type="primary" htmlType="submit" icon={<PlusOutlined/>} disabled={isAdicionarNfBloqueado} className="botao-acao-class">
                                Adicionar NF
                            </Button>
                        </div>
                    </section>

                    <section className="section-card" aria-label="Lista Nota Fiscal">
                        <div className="section-card__head">
                            <h2 className="section-card__title">Lista Nota Fiscal</h2>
                            <span className="section-card__hint">{resolverDicaListaNotas()}</span>
                        </div>
                        <div className="section-card__tabela">
                            <Table
                                dataSource={formRequestNotasDto}
                                columns={columnsListNFUpload}
                                rowKey="chaveNfe"
                                rowClassName={(record) => resolverClasseLinhaNota(record.chaveNfe)}
                                bordered={false}
                                pagination={false}
                                locale={{emptyText: <Empty description="Nenhuma nota fiscal adicionada" image={Empty.PRESENTED_IMAGE_SIMPLE}/>}}
                            />
                        </div>
                        <div className="section-card__actions">
                            <span className="section-card__actions-note">
                                {hasNotasNaLista
                                    ? `${formRequestNotasDto.length === 1 ? 'A nota acima entra' : `As ${formRequestNotasDto.length} notas acima entram`} no bloco ${tipoFrete}${isFob && formRequestFobDto?.numeroConhecimento ? ` do conhecimento ${formRequestFobDto.numeroConhecimento}` : ''}.`
                                    : 'Adicione ao menos uma nota para registrar um bloco CIF/FOB.'}
                            </span>
                            <Button type="primary" icon={<FolderAddOutlined/>} onClick={handleGeralAdd} className="botao-acao-class">
                                Adicionar CIF/FOB
                            </Button>
                        </div>
                    </section>
                </Flex>
            </Form>

            <section className="section-card" aria-label="Lista de CIF/FOB">
                <div className="section-card__head">
                    <h2 className="section-card__title">Lista de CIF/FOB</h2>
                    <span className="section-card__hint">
                        {hasBlocosCifFob
                            ? `${formRequestGeralDto.length} ${pluralizar(formRequestGeralDto.length, 'bloco adicionado', 'blocos adicionados')}`
                            : 'Nenhum bloco adicionado'}
                    </span>
                </div>
                <div className="section-card__tabela">
                    <Table
                        dataSource={formRequestGeralDto}
                        columns={columnsListNotasUpload}
                        rowKey="numeroConhecimento"
                        bordered={false}
                        pagination={false}
                        locale={{
                            emptyText: (
                                <div className="section-card__empty">
                                    <InboxOutlined style={{fontSize: 28}}/>
                                    <span className="section-card__empty-title">Nenhum CIF/FOB adicionado</span>
                                    <span className="section-card__empty-next">Use <strong>Adicionar CIF/FOB</strong> acima para agrupar as notas. É esse bloco que libera a finalização.</span>
                                </div>
                            ),
                        }}
                    />
                </div>
            </section>

            <div className="commit-bar">
                <div className="commit-bar__resumo">
                    <span><span className="commit-bar__forte">{totalNotas}</span> {pluralizar(totalNotas, 'nota fiscal', 'notas fiscais')}</span>
                    <span><span className="commit-bar__forte">{totalVolumes}</span> volumes</span>
                    <span><span className="commit-bar__forte">{formRequestGeralDto.length}</span> {pluralizar(formRequestGeralDto.length, 'bloco', 'blocos')} CIF/FOB</span>
                    <span><span className="commit-bar__forte">{totalAnexos}</span> {pluralizar(totalAnexos, 'anexo', 'anexos')}</span>
                </div>
                <div className="commit-bar__acoes">
                    {isFinalizarBloqueado && (
                        <span className="commit-bar__bloqueio">
                            <ExclamationCircleOutlined/>
                            Deve conter pelo menos 1 Nota em um bloco CIF/FOB.
                        </span>
                    )}
                    <Button type="primary" size="large" onClick={handleGeralFinish} icon={<CheckOutlined/>}
                            loading={concluirMutation.isPending} disabled={isFinalizarBloqueado} className="botao-acao-class">
                        Finalizar Agendamento
                    </Button>
                </div>
            </div>

            <input
                ref={inputXmlConferenciaRef}
                type="file"
                accept=".xml,text/xml,application/xml"
                aria-label="Anexar XML para conferir o destinatário da Nota Fiscal"
                tabIndex={-1}
                hidden={true}
                onChange={handleXmlConferenciaChange}
            />
        </div>
    );
};
